import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import communicationLogService from '../../../api/services/communicationLogService';
import candidateService from '../../../api/services/candidateService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import Alert from '../../../components/common/Alert/Alert';
import { API_BASE_URL } from '../../../api/config';
import {
  ArrowLeft,
  Paperclip,
  Mail,
  Phone,
  Calendar,
  FileText,
  AlertCircle,
  Download,
  MessageSquare,
  Video
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const ViewCommunication = ({ onBack, id: propId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [communication, setCommunication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [offer, setOffer] = useState(null);

  // Load communication data
  const loadCommunication = async () => {
    try {
      setIsLoading(true);


      const result = await communicationLogService.getCommunicationLogById(id);


      if (result && (result.responseCode === 200 || result.responseCode === 201)) {
        const commData = result.data;


        setCommunication(commData);

        // Log attachment data

        if (commData.attachments && commData.attachments.length > 0) {
          commData.attachments.forEach((att, index) => {
            console.log(`📎 Attachment ${index}:`, {
              name: att.name,
              url: att.url,
              type: att.type,
              size: att.size
            });
          });
        }


        // Load candidate data if available
        if (commData.candidateId) {
          try {
            // Handle both string and object candidateId
            const candidateId = typeof commData.candidateId === 'object'
              ? commData.candidateId._id || commData.candidateId.id
              : commData.candidateId;

            if (candidateId) {

              const candidateResult = await candidateService.getCandidateById(candidateId);
              if (candidateResult && (candidateResult.responseCode === 200 || candidateResult.responseCode === 201)) {
                setCandidate(candidateResult.data);
              }
            }
          } catch (error) {

          }
        }

        // Load offer data if available (you might need to create this API)
        if (commData.offerId) {
          // TODO: Implement offer loading when offer API is available
          setOffer({
            id: commData.offerId,
            title: commData.jobTitle || 'N/A'
          });
        }

      } else {

        showError('Failed to load communication details');
      }
    } catch (error) {

      showError('Error loading communication details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadCommunication();
    }
  }, [id]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-[#0F2942] bg-blue-50 border-blue-200';
      case 'delivered': return 'text-green-600 bg-green-50 border-green-200';
      case 'read': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'replied': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'video_call': return <Video className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {


      // Check if attachment has a valid URL
      if (!attachment.url) {

        showError('Attachment URL not found. Please contact support.');
        return;
      }

      // Check if URL is empty string
      if (attachment.url.trim() === '') {

        showError('Attachment URL is empty. Please contact support.');
        return;
      }

      // Validate URL format
      try {
        new URL(attachment.url);

      } catch (urlError) {

        showError('Invalid attachment URL format. Please contact support.');
        return;
      }

      // Show loading state
      showSuccess(`Preparing download for ${attachment.name}...`);

      // Try to fetch as blob first to avoid browser PDF viewer/CORS issues
      try {
        // Use backend proxy to download with Authorization header
        const base = API_BASE_URL;
        const proxyUrl = `${base}/communication-logs/download?url=${encodeURIComponent(attachment.url)}&name=${encodeURIComponent(attachment.name || 'attachment')}`;

        const token = localStorage.getItem('auth_token');
        const resp = await fetch(proxyUrl, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`Download failed: ${resp.status} ${text}`);
        }
        const blob = await resp.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = attachment.name || 'attachment';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(objectUrl);
        showSuccess(`Downloading ${attachment.name || 'attachment'}...`);
      } catch (blobError) {

        showError('Failed to download attachment via proxy.');
      }

    } catch (error) {

      showError(`Failed to download ${attachment.name}. Please try again.`);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942]"></div>
      </div>
    );
  }

  if (!communication) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Communication not found</h3>
        <p className="text-gray-500 mb-4">The requested communication could not be found.</p>
        <Button variant="outline" onClick={handleBack}>
          Back to Communications
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 break-words">Communication Details</h1>
            <p className="text-gray-600 mt-1 break-words">View offer-related communication</p>
          </div>
        </div>
        <div className="flex space-x-3">

        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Communication Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Subject and Basic Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 break-all">
                {communication.subject}
              </h2>
            </div>

            {/* Participants */}
            <div className="space-y-3 mb-6">

              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 w-12">To:</span>
                <span className="text-sm text-gray-900 break-all">{communication.recipient}</span>
              </div>
              {communication.cc && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700 w-12">CC:</span>
                  <span className="text-sm text-gray-900 break-all">{communication.cc}</span>
                </div>
              )}
              {communication.bcc && (
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700 w-12">BCC:</span>
                  <span className="text-sm text-gray-900 break-all">{communication.bcc}</span>
                </div>
              )}
            </div>

            {/* Message Content */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Message</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-gray-700 whitespace-pre-wrap break-words">
                  {communication.message}
                </div>
              </div>
            </div>
          </div>

          {/* Attachments */}
          {communication.attachments && communication.attachments.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Paperclip className="h-5 w-5 mr-2" />
                Attachments ({communication.attachments.length})
              </h3>
              <div className="space-y-3">
                {communication.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-blue-100/60 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {attachment.type?.includes('pdf') ? (
                          <FileText className="h-6 w-6 text-red-500" />
                        ) : attachment.type?.includes('image') ? (
                          <FileText className="h-6 w-6 text-blue-500" />
                        ) : attachment.type?.includes('word') || attachment.type?.includes('document') ? (
                          <FileText className="h-6 w-6 text-[#0F2942]" />
                        ) : attachment.type?.includes('excel') || attachment.type?.includes('spreadsheet') ? (
                          <FileText className="h-6 w-6 text-green-500" />
                        ) : (
                          <Paperclip className="h-6 w-6 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 break-all">
                          {attachment.name}
                        </p>
                        <p className="text-xs text-gray-500 break-words">
                          {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : 'Unknown size'}
                          {attachment.type && ` • ${attachment.type}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          handleDownloadAttachment(attachment);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm"
                        title="Download attachment"
                      >
                        <Download className="h-4 w-4" />
                        <span className="text-sm font-medium">Download</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Related Records */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Related Records</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Candidate</label>
                <p className="text-sm text-gray-900 break-words capitalize">
                  {communication.candidateName ||
                    (typeof communication.candidateId === 'object'
                      ? `${communication.candidateId.firstName || ''} ${communication.candidateId.lastName || ''}`.trim()
                      : '') ||
                    (candidate ? `${candidate.firstName} ${candidate.lastName}` : 'N/A')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Offer</label>
                <p className="text-sm text-gray-900 break-words">{communication.jobTitle || offer?.title || 'N/A'}</p>
              </div>
            </div>
          </div>


          {/* Tags */}
          {communication.tags && communication.tags.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {communication.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {communication.notes && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
              <p
                className="text-sm text-gray-700 whitespace-pre-wrap break-all first-letter:capitalize"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              >
                {communication.notes}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Alert Component */}
      <Alert
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm || hideAlert}
        onCancel={hideAlert}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default ViewCommunication;
